import React from 'react';
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem, ListItemText, Typography,
} from '@mui/material';
import { ErrorOutline as ErrorIcon } from '@mui/icons-material';
import { LocalizeContextProps, withLocalize } from 'react-localize-redux';
import { MissingRequiredField } from '../../utils/fieldGroupEdit';

/**
 * SKIN-536 — shown instead of a browser alert when an eCRF is saved with required
 * fields empty. Lists ALL of them (the alert showed only the first), grouped by
 * section, and lets the clinician jump to each one.
 */
interface MissingRequiredFieldsModalProps extends LocalizeContextProps {
    open: boolean;
    missing: MissingRequiredField[];
    onJumpTo: (field: MissingRequiredField) => void;
    onClose: () => void;
    /** SKIN-543 — translation ids for the intro and the close button, when not saving a form */
    introId?: string;
    backId?: string;
}

function whereInForm(field: MissingRequiredField): string {
    const group = field.instanceIndex ? `${field.groupName} #${field.instanceIndex}` : field.groupName;
    return field.subformOf ? `${group} › ${field.subformOf}` : group;
}

const MissingRequiredFieldsModal: React.FC<MissingRequiredFieldsModalProps> = ({
    open, missing, onJumpTo, onClose, translate,
    introId = 'hospital.fg-edit-missing-intro', backId = 'hospital.fg-edit-missing-back',
}) => {
    const t = (id: string, data?: Record<string, unknown>) => translate(id, data as any) as string;
    const sections: { name: string; fields: MissingRequiredField[] }[] = [];
    for (const field of missing) {
        const name = field.sectionName || '';
        let section = sections.find(s => s.name === name);
        if (!section) {
            section = { name, fields: [] };
            sections.push(section);
        }
        section.fields.push(field);
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="missing-required-title">
            <DialogTitle id="missing-required-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorIcon color="error" />
                {t('hospital.fg-edit-missing-title')}
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    {t(introId, { count: missing.length })}
                </Typography>
                {sections.map(section => (
                    <React.Fragment key={section.name}>
                        {section.name && (
                            <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 600 }}>{section.name}</Typography>
                        )}
                        <List dense disablePadding>
                            {section.fields.map(field => (
                                <ListItem key={`${field.groupId}-${field.instanceNumber}-${field.fieldId}-${field.triggerOptionId ?? ''}`}
                                    disableGutters
                                    secondaryAction={
                                        <Button size="small" onClick={() => onJumpTo(field)}
                                            aria-label={t('hospital.fg-edit-missing-go-to', { field: field.fieldLabel })}>
                                            {t('hospital.fg-edit-missing-go')}
                                        </Button>
                                    }>
                                    <ListItemText
                                        primary={field.fieldLabel}
                                        secondary={whereInForm(field)} />
                                </ListItem>
                            ))}
                        </List>
                    </React.Fragment>
                ))}
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={onClose}>{t(backId)}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default withLocalize(MissingRequiredFieldsModal);
